import config from "@/config";
import Joi from "joi";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AppContext } from "@/App.context";

export const PERMISSIONS = {
  read: 1,
  edit: 2,
};

/**
 * @typedef PostUpdatePrivilegeAccountRGItem
 * @property {string=} resourceGroupId
 * @property {string=} resourceGroupName
 * @property {number} permission
 */

/**
 * @typedef PostUpdatePrivilegeAccountItem
 * @property {number=} id
 * @property {string} accountId
 * @property {string} accountName
 * @property {string} type
 * @property {number} permission
 * @property {PostUpdatePrivilegeAccountRGItem[]} resourceGroups
 */

/**
 * @typedef UpdatePrivilegeReq
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {PostUpdatePrivilegeAccountItem[]} accounts
 */

/**
 * @typedef UpdatePrivilegeRes
 * @property {*} privilege
 */

/** @type {Joi<UpdatePrivilegeReq>} */
export const UpdatePrivilegeReq = Joi.object({
  name: Joi.string(),
  description: Joi.string().allow("").optional(),
  accounts: Joi.array()
    .items(
      Joi.object({
        accountId: Joi.string(),
        accountName: Joi.string(),
        permission: Joi.number().valid(
          PERMISSIONS.read,
          PERMISSIONS.edit,
          null
        ),
        type: Joi.string().valid("aws", "azure", "gcp"),
        resourceGroups: Joi.array().items(
          Joi.object({
            resourceGroupName: Joi.string(),
            resourceGroupId: Joi.string(),
            permission: Joi.number().valid(
              PERMISSIONS.read,
              PERMISSIONS.edit,
              null
            ),
          }).optional()
        ),
      })
    )
    .messages({
      "array.min": "Selected accounts list cannot be empty",
    }),
});

/**
 * @typedef UpdatePrivilegeRes
 * @property {*} privilege
 */

export const updatePrivilege = (token) => {
  /**
   * @param {UpdatePrivilegeReq} req
   * @returns {Promise<UpdatePrivilegeRes>}
   */
  return async (req) => {
    const { id, ...body } = req;
    const totalStats = await fetch(
      `${config.apiHost}/v1/privileges/${id}/update-privilege`,
      {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );
    if (!totalStats.ok) {
      throw new Error((await totalStats.json()).message);
    }
    const json = await totalStats.json();
    return json;
  };
};

/**
 * @param {any[]=} deps
 */
export const useUpdatePrivilege = (deps = []) => {
  const appContext = useContext(AppContext);
  const mutation = useMutation({
    mutationKey: ["updatePrivilege", ...deps],
    mutationFn: updatePrivilege(appContext.idToken),
  });
  return mutation;
};
